import React, { useEffect, useMemo } from "react";

import { format } from "date-fns";
import en from "date-fns/locale/en-US";
import ro from "date-fns/locale/ro";
import { Label } from "devextreme-react/data-grid";
import Form, {
  ButtonItem,
  ColCountByScreen,
  EmptyItem,
  FormRef,
  GroupItem,
  RangeRule,
  RequiredRule,
  SimpleItem,
  Tab,
  TabbedItem,
  TabPanelOptions,
} from "devextreme-react/form";
import { dinero, subtract, toDecimal } from "dinero.js";
import sum from "hash-sum";
import { isEmpty, max, set } from "lodash";
import { Case, CaseElse, Switch } from "react-context-switch";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";
import { getIdFromAction } from "starfx";

// import { useLoader } from 'starfx/react';
import {
  useFxDispatch as useDispatch,
  useFxSelector as useSelector,
} from "@app/statefx";
import { schema } from "@app/statefx/schema";
import {
  convertAmmountToDineroFn,
  convnertDinero,
  dineros,
  selectAccountsByEmitent,
  selectClosestExchangeRate,
} from "@app/statefx/selectors/currency";
import { selectCurentInvoiceForPayments } from "@app/statefx/selectors/ds-collection.sel";
import { selectCollectionInEdit } from "@app/statefx/selectors/invoiceOverall.sel";
import { applog } from "@app/statefx/xThunks/app-log";
import {
  deleteFiscalInvoicePayment,
  processFiscalInvoicePayment,
} from "@app/statefx/xThunks/ds-colection";
import { setEditingCollection } from "@app/statefx/xThunks/uiInvoiceOverall";
import "@app/style/dx.generic.catetory-chooser-compact.css";
import "@app/style/dx.generic.catetory-chooser.css";
import {
  EnuAppOps,
  TCollectForm,
  TCurrencyRecordSet,
  TIFCollectionItem,
  TIvFiscal,
} from "@app/types/insider";

import { IFCollectionHistory } from "./cHistory";
import "./collect.scss";

import type { TCurrency } from "@app/types";
import type { TAccount } from "@app/types/insider";
import type { Operation } from "effection";

const dateLocales = { ro: ro, en: en };

const _dataSet = {
  INCASARE_ID: -1,
  FF_ID: -1,
  DATA_I: new Date(),
  VALOARE_I: 0,
  MONEDA_I: "RON",
  CONTMAID: 0,
  OBS_I: "",
  TIP_I: "P",
  STARE_I: "ACTIVA",
  FL_ID: 0,
  OPTIUNE: -1, // -1 curs oficial,  0 - curs fiscala 1- curs proforma, 2 curs special
  OPTIUNE_FORM: "", // curs_eur, moneda_curs.
  CURS_SPECIAL: 0,
  MONEDA_CURS_SPECIAL: "RON",
  CS_EUR: 0,
  CS_USD: 0,
  CS_GBP: 0,
  CS_CHF: 0,
} as TCollectForm;

const cursProforma = {
  RON: {
    amount: 1 * 10_000,
    scale: 4,
  },
  EUR: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  USD: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  GBP: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  CHF: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  MDL: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
};
// CURSFCTF //
const cursFiscal = {
  RON: {
    amount: 1 * 10_000,
    scale: 4,
  },

  EUR: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  USD: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  GBP: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  CHF: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
  MDL: {
    amount: 4.92 * 10_000,
    scale: 4,
  },
};

const currencyForm = (currencySet: TCurrencyRecordSet) => {
  return (
    <div className="dx-fieldset">
      <div className="dx-field">
        <div className="dx-field-label">RON</div>
        <div className="dx-field-value-static">
          {toDecimal(
            dinero({
              ...currencySet.RON,
              currency: dineros["RON"],
            })
          )}
        </div>
      </div>
      <div className="dx-field">
        <div className="dx-field-label">EUR</div>
        <div className="dx-field-value-static">
          {toDecimal(
            dinero({
              ...currencySet.EUR,
              currency: dineros["EUR"],
            })
          )}
        </div>
      </div>
      <div className="dx-field">
        <div className="dx-field-label">USD</div>
        <div className="dx-field-value-static">
          {toDecimal(
            dinero({
              ...currencySet.USD,
              currency: dineros["USD"],
            })
          )}
        </div>
      </div>
      <div className="dx-field">
        <div className="dx-field-label">GBP</div>
        <div className="dx-field-value-static">
          {toDecimal(
            dinero({
              ...currencySet.GBP,
              currency: dineros["GBP"],
            })
          )}
        </div>
      </div>
      <div className="dx-field">
        <div className="dx-field-label">CHF</div>
        <div className="dx-field-value-static">
          {toDecimal(
            dinero({
              ...currencySet.CHF,
              currency: dineros["CHF"],
            })
          )}
        </div>
      </div>
    </div>
  );
};

function mapCollectionToForm(obj: TIFCollectionItem): TCollectForm {
  return {
    INCASARE_ID: obj.IFF_ID, // (!important)
    FF_ID: obj.FF_ID,
    DATA_I: obj.DATA_I,
    VALOARE_I: obj.VALOARE_I,
    MONEDA_I: obj.MONEDA_I,
    CONTMAID: obj.CONTMAID,
    OBS_I: obj.OBS_I,
    TIP_I: (obj.TIP_I || "").trim(),
    STARE_I: obj.STARE_I,
    FL_ID: obj.FL_ID,
    OPTIUNE: obj.OPTIUNE,
    OPTIUNE_FORM: "", // Default value, replace with actual mapping if exists
    CURS_SPECIAL: obj.CURS_SPECIAL,
    MONEDA_CURS_SPECIAL: obj.MONEDA_CURS_SPECIAL,
    CS_EUR: obj.CS_EUR,
    CS_USD: obj.CS_USD,
    CS_GBP: obj.CS_GBP,
    CS_CHF: obj.CS_CHF,
  };
}

export const CollectForm = () => {
  const [ref, { _, width }] = useMeasure();
  const collectRef = React.useRef<FormRef>(null);
  const listenRef = React.useRef<boolean>(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  /// this must be a this level, otherwise, if if closer to the root, it causes unwanted re-renders

  const data: TIvFiscal = useSelector(selectCurentInvoiceForPayments);
  const sumDataset = useMemo(() => sum(data), [data]);

  const [dataSet, setDataSet] = React.useState<TCollectForm>(_dataSet);
  const payEdit = useSelector(selectCollectionInEdit) as TIFCollectionItem;

  const payStr = useMemo(() => JSON.stringify(payEdit), [payEdit]);
  const dataStr = useMemo(() => JSON.stringify(data), [data]);
  const isNewRecordMode = useMemo(() => {
    return !payEdit || (payEdit && Object.keys(payEdit).length === 0);
  }, [payEdit]);

  // note: issue with selectById in the useFxLoader...
  // const updateLoader = useFxLoader(processFiscalInvoicePayment);

  const id = getIdFromAction(processFiscalInvoicePayment);
  const updateLoader_ = useSelector(schema.loaders.selectTable);
  const updateLoader = useMemo(() => updateLoader_?.[id], [updateLoader_]);

  const cols = 1;
  // useMemo(() => {
  //   return width > 999 ? 2 : 1;
  // }, [width]);

  const tabCollectionTitle = useMemo(() => {
    return isNewRecordMode ? "Incasare noua" : "VIZUALIZARE";
  }, [isNewRecordMode]);

  const closestRate_ = useSelector((s) =>
    selectClosestExchangeRate(s, {
      date: new Date(dataSet.DATA_I).toDateString(),
    })
  );
  const closestRate = useMemo(() => {
    if (Object.keys(closestRate_).length === 0) {
      return cursFiscal;
    }
    return closestRate_;
  }, [closestRate_]);
  const theAccounts: TAccount[] = useSelector((s) =>
    selectAccountsByEmitent(s, { ABR: data.ABR })
  );

  const theCurrecies = useSelector(schema.currencies.select);
  //todo: https://github.com/VldMrgnn/jk-react-app/issues/112 bring all the exchange rate types from the store
  const ivFiscalRate = useMemo(() => cursFiscal, []);
  const ivProformaRate = useMemo(() => cursProforma, []);

  // const updateLoader = useLoader(processFiscalInvoicePayment);

  const crtSerialDate = useMemo(() => {
    // some debug checks //
    if (!data) return "";
    if (isEmpty(data)) return "";
    if (!data.NR_FF) return "";
    if (!data.LA_DATA) return "";
    if (!data.CLIENT) return "";
    const ivoice = `${data.NR_FF}/${format(
      new Date(data.LA_DATA),
      "yyyy-MM-dd"
    )}`;

    const client = data.CLIENT;
    const ammount = `${data?.[`TOTAL_${data?.MONEDA_FF}_TXT`] || "N/A"}`;
    return `${client} - ${ivoice}, Total fct: ${ammount}`;
  }, [sumDataset]);

  const autoCompleteForm = () => {
    // const data = collectRef.current?.instance().option("formData");
    // const newDataSet = { ...dataSet, ...data };
    // setDataSet(newDataSet);
    const accId = (theAccounts || []).find(
      (a: TAccount) => a.MONEDA === data.MONEDA_FF
    );
    const newDataSet = {
      ...dataSet,
      VALOARE_I: data?.REST_DE_INCASAT,
      MONEDA_I: data.MONEDA_FF,
      CONTMAID: accId?.CONTMA_ID || 0,
      OBS_I: "",
      TIP_I: "I",
      STARE_I: "ACHITATA",
      FL_ID: 0,
      OPTIUNE: -1, // -1 curs oficial,  0 - curs fiscala 1- curs proforma, 2 curs special
      OPTIUNE_FORM: "", // curs_eur, moneda_curs.
      CURS_SPECIAL: data?.MONEDA_FF
        ? +toDecimal(
            convertAmmountToDineroFn(
              1,
              data.MONEDA_FF as TCurrency,
              "RON",
              closestRate as TCurrencyRecordSet
            ),
            ({ value }) => Number(value || 0).toFixed(4)
          )
        : 0,
      MONEDA_CURS_SPECIAL: data.MONEDA_FF || "RON",
      CS_EUR: closestRate?.EUR?.amount / 10_000,
      CS_USD: closestRate?.USD?.amount / 10_000,
      CS_GBP: closestRate?.GBP?.amount / 10_000,
      CS_CHF: closestRate?.CHF?.amount / 10_000,
    } as TCollectForm;
    setDataSet(newDataSet);
  };

  const autoGetReady = () => {
    const accId = (theAccounts || []).find(
      (a: TAccount) => a.MONEDA === data.MONEDA_FF
    );
    const newDataSet = {
      ...dataSet,
      VALOARE_I: 0,
      MONEDA_I: data.MONEDA_FF,
      CONTMAID: accId?.CONTMA_ID || 0,
      OBS_I: "",
      TIP_I: "I",
      STARE_I: "ACTIVA",
      FL_ID: 0,
      OPTIUNE: -1, // -1 curs oficial,  0 - curs fiscala 1- curs proforma, 2 curs special
      OPTIUNE_FORM: "", // curs_eur, moneda_curs.
      CURS_SPECIAL: data?.MONEDA_FF
        ? +toDecimal(
            convertAmmountToDineroFn(
              1,
              data.MONEDA_FF as TCurrency,
              "RON",
              closestRate as TCurrencyRecordSet
            ),
            ({ value }) => Number(value || 0).toFixed(4)
          )
        : 0,
      MONEDA_CURS_SPECIAL: data.MONEDA_FF || "RON",
      CS_EUR: closestRate?.EUR.amount / 10_000,
      CS_USD: closestRate?.USD.amount / 10_000,
      CS_GBP: closestRate?.GBP.amount / 10_000,
      CS_CHF: closestRate?.CHF.amount / 10_000,
    } as TCollectForm;
    setDataSet(newDataSet);
  };

  const calculateDiff = () => {
    const rateOptions = {
      "-1": closestRate,
      "0": ivFiscalRate,
      "1": ivProformaRate,
      "2": {
        ...closestRate,
        [dataSet.MONEDA_CURS_SPECIAL]: {
          amount: dataSet.CURS_SPECIAL * 10_000,
          scale: 4,
        },
      },
    };

    const theRates =
      rateOptions[`${dataSet.OPTIUNE}` as "-1" | "0" | "1" | "2"] ||
      closestRate;

    const theRestInRON = convertAmmountToDineroFn(
      data?.REST_DE_INCASAT,
      data.MONEDA_FF as TCurrency,
      "RON",
      theRates as TCurrencyRecordSet
    );

    const theCurrentInRON = convertAmmountToDineroFn(
      dataSet.VALOARE_I,
      dataSet.MONEDA_I as TCurrency,
      "RON",
      theRates as TCurrencyRecordSet
    );

    const theDiff = subtract(theRestInRON, theCurrentInRON);
    const theDiffInInvoiceCurrency = convnertDinero(
      theDiff,
      dineros[data.MONEDA_FF || ("RON" as TCurrency)],
      theRates as TCurrencyRecordSet
    );
    return toDecimal(
      theDiffInInvoiceCurrency,
      ({ value, currency }) => `${Number(value).toFixed(2)} ${currency.code}`
    );
  };

  const onFieldDataChanged = (e: any) => {
    dispatch(
      applog({
        operation: EnuAppOps.collection_start_editing,
        value: sumDataset,
      })
    );
    // const u = e.component.option();
    // console.log('u', u);
    const data = e.component.option("formData");
    const sdata = structuredClone(data);
    // some auto behavior
    if (e.dataField === "MONEDA_I") {
      // current currecy of account
      const accId = (theAccounts || []).find(
        (a: TAccount) => a.CONTMA_ID === dataSet.CONTMAID
      );
      if (accId?.MONEDA !== e.value) {
        //the currency of account is dfiferent from the currency of the collection item
        // we need to change the account to the one with the same currency
        const newAccout = (theAccounts || []).find(
          (a: TAccount) => a.MONEDA === e.value
        );
        if (newAccout) {
          set(sdata, "CONTMAID", newAccout.CONTMA_ID);
        } else {
          // no account with the same currency found
          set(sdata, "CONTMAID", null);
        }
      }
    }
    const newDataSet = { ...dataSet, ...sdata };
    setDataSet(newDataSet);
    collectRef.current?.instance().validate();
  };

  const saveClick = async (e: any) => {
    const result = await collectRef.current?.instance().validate();

    if (result?.isValid) {
      listenRef.current = true;
      const ldata = {
        ...dataSet,
        DATA_I: format(new Date(dataSet.DATA_I), "yyyy-MM-dd"),
      };
      dispatch(processFiscalInvoicePayment(ldata as TCollectForm));
    }
  };

  const saveButtonOptions = {
    text: "Salvarea incasarii",
    type: "success" as const,
    useSubmitBehavior: false,
    onClick: saveClick,
  };

  useEffect(() => {
    if (listenRef.current) {
      if (updateLoader?.status === "success") {
        // reset the form
        autoGetReady();
      }
    }
  }, [updateLoader?.status]);

  useEffect(() => {
    const accId = (theAccounts || []).find(
      (a: TAccount) => a.MONEDA === data.MONEDA_FF
    );
    if (payEdit && Object.keys(payEdit).length > 0) {
      setDataSet(mapCollectionToForm(payEdit));

      collectRef.current.instance().repaint();
    } else {
      setDataSet({
        ..._dataSet,
        FF_ID: data.FF_ID,
        DATA_I: new Date(),
        VALOARE_I: 0,
        MONEDA_I: data.MONEDA_FF,
        CONTMAID: accId?.CONTMA_ID || 0,
        OBS_I: "",
        TIP_I: data.REST_DE_INCASAT === 0 ? "I" : "P",
        STARE_I:
          data.ANULATA === 1
            ? "Anulată"
            : data.REST_DE_INCASAT === 0
            ? "Achitată"
            : "Activă",
        FL_ID: 0,
        OPTIUNE: -1, // -1 curs oficial,  0 - curs fiscala 1- curs proforma, 2 curs special
        OPTIUNE_FORM: "", // curs_eur, moneda_curs.
        CURS_SPECIAL: data?.MONEDA_FF
          ? +toDecimal(
              convertAmmountToDineroFn(
                1,
                data.MONEDA_FF as TCurrency,
                "RON",
                closestRate as TCurrencyRecordSet
              ),
              ({ value }) => Number(value || 0).toFixed(4)
            )
          : 0,
        MONEDA_CURS_SPECIAL: data.MONEDA_FF,
        CS_EUR: closestRate?.EUR?.amount / 10_000,
        CS_USD: closestRate?.USD?.amount / 10_000,
        CS_GBP: closestRate?.GBP?.amount / 10_000,
        CS_CHF: closestRate?.CHF?.amount / 10_000,
      });
    }
  }, [dataStr, payStr]);

  return (
    <Switch value={data.FF_ID}>
      <Case when={-1}>
        <div>{"Incasari - form pentru incasari multiple"}</div>
      </Case>
      <CaseElse>
        <div className={"dx-card collect"}>
          <div ref={ref} className="paneltitle">
            {crtSerialDate}
          </div>
          <Form
            ref={collectRef}
            formData={dataSet}
            id="collectForm"
            style={{ maxWidth: "670px" }}
            showValidationSummary={false}
            validationGroup="collectForm"
            className="dx-swatch-catetory-chooser-compact"
            visible={width > 300}
            onFieldDataChanged={onFieldDataChanged}
            readOnly={!isNewRecordMode}
            activeStateEnabled={true}
          >
            <GroupItem>
              <TabbedItem>
                <TabPanelOptions deferRendering={false} />
                <Tab
                  title={tabCollectionTitle}
                  icon={isNewRecordMode ? "money" : "photo"}
                  colCount={cols}
                >
                  <GroupItem colCount={2}>
                    <GroupItem colCount={3} colSpan={1}>
                      <ButtonItem
                        colSpan={2}
                        horizontalAlignment="left"
                        buttonOptions={{
                          text: t("collection_auto_complete"),
                          type: "default",
                          stylingMode: "outlined",
                          useSubmitBehavior: false,
                          onClick: autoCompleteForm,
                          disabled: !isNewRecordMode,
                        }}
                      />

                      <ButtonItem
                        buttonOptions={{
                          icon: "refresh",
                          type: "normal",
                          stylingMode: "outlined",
                          useSubmitBehavior: false,
                          hint: t("collection_reset"),
                          onClick: setDataSet.bind(null, _dataSet),
                          disabled: !isNewRecordMode,
                        }}
                      />
                    </GroupItem>
                    <SimpleItem
                      render={(e) => {
                        const outstanding = data?.[`REST_DE_INCASAT_TXT`];
                        return (
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              flexWrap: "nowrap",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <label
                              style={{
                                marginTop: "auto",
                                justifyContent: "flex-end",
                              }}
                            >
                              {isNewRecordMode
                                ? `${t("collection_auto_before")}:`
                                : ""}
                            </label>
                            <span
                              className={
                                data.REST_DE_INCASAT > 0
                                  ? "c-outstanding"
                                  : "c-collected"
                              }
                            >
                              {outstanding}
                            </span>
                          </div>
                        );
                      }}
                    />
                    <SimpleItem
                      colSpan={4}
                      dataField="DATA_I"
                      editorType="dxDateBox"
                      editorOptions={{
                        type: "date",
                        displayFormat: "dd MMMM yyyy",
                        pickerType: "calendar",
                        showClearButton: false,
                        useMaskBehavior: true,
                      }}
                    >
                      <Label text={t("collection_date")} />
                      <RequiredRule />
                    </SimpleItem>

                    <SimpleItem
                      render={() => {
                        const diff = calculateDiff();
                        return (
                          <div
                            className="collection-rate-display"
                            style={{
                              width: "100%",
                              display: "flex",
                              flexWrap: "nowrap",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <label className="collection-rate-label">
                              {isNewRecordMode
                                ? `${t("collection_auto_final")}:`
                                : ""}
                            </label>
                            <span
                              className={
                                ["-", "0"].some((s) => diff.startsWith(s))
                                  ? "c-collected"
                                  : "c-outstanding"
                              }
                            >
                              {diff}
                            </span>
                          </div>
                        );
                      }}
                    />
                    <SimpleItem
                      dataField="VALOARE_I"
                      editorType="dxNumberBox"
                      editorOptions={{
                        format: "###,##0.00",
                        showClearButton: false,
                        useMaskBehavior: true,
                      }}
                    >
                      <Label text={t("collection_value")} />
                      <RequiredRule />
                    </SimpleItem>
                    <SimpleItem
                      dataField="MONEDA_I"
                      editorType="dxSelectBox"
                      editorOptions={{
                        items: theCurrecies,
                        showClearButton: false,
                        useMaskBehavior: true,
                      }}
                    >
                      <Label text={t("collection_currency")} />
                      <RequiredRule />
                    </SimpleItem>

                    <SimpleItem
                      colSpan={2}
                      dataField="CONTMAID"
                      editorType="dxSelectBox"
                      editorOptions={{
                        items: theAccounts,
                        showClearButton: true,
                        useMaskBehavior: true,
                        valueExpr: "CONTMA_ID",
                        displayExpr: "NR_CONT",
                      }}
                    >
                      <Label text={t("collection_account")} />
                      <RequiredRule />
                      <RangeRule
                        min={1}
                        message={t("select_a_valid_account")}
                      />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={2}
                      dataField="OBS_I"
                      editorType="dxTextArea"
                      editorOptions={{
                        height: 60,
                        showClearButton: true,
                        useMaskBehavior: true,
                      }}
                    >
                      <Label text={t("collection_observation")} />
                    </SimpleItem>

                    <SimpleItem
                      dataField="TIP_I"
                      editorType="dxSelectBox"
                      editorOptions={{
                        items: [
                          { text: "Partial", value: "P" },
                          { text: "Integral", value: "I" },
                        ],
                        showClearButton: true,
                        useMaskBehavior: true,
                        defaultValue: data?.REST_DE_INCASAT === 0 ? "I" : "P",
                        valueExpr: "value",
                        displayExpr: "text",
                      }}
                    >
                      <Label text={t("collection_type")} />
                      <RequiredRule />
                    </SimpleItem>
                    <SimpleItem
                      dataField="STARE_I"
                      editorType="dxSelectBox"
                      editorOptions={{
                        items: [
                          { text: "Achitată", value: "ACHITATA" },
                          { text: "Activă", value: "ACTIVA" },
                          { text: "Anulată", value: "ANULATA" },
                        ],
                        valueExpr: "value",
                        displayExpr: "text",
                        showClearButton: true,
                        useMaskBehavior: true,
                        defaultValue:
                          data?.REST_DE_INCASAT === 0 ? "ACHITATA" : "ACTIVA",
                      }}
                    >
                      <Label text={t("collection_status")} />
                      <RequiredRule />
                    </SimpleItem>
                    <SimpleItem dataField="FL_ID" visible={false} />
                  </GroupItem>

                  <GroupItem colSpan={1} colCount={2}>
                    <GroupItem caption={t("collection_rate")} colCount={1}>
                      <SimpleItem
                        dataField="OPTIUNE"
                        editorType="dxRadioGroup"
                        editorOptions={{
                          valueExpr: "value",
                          displayExpr: "text",
                          items: [
                            { text: t("daily_rate"), value: -1 },
                            { text: t("invoiceproforma_rate"), value: 1 },
                            { text: t("invoicefiscal_rate"), value: 0 },
                            { text: t("custom_rate"), value: 2 },
                          ],
                          defaultItem: -1,
                        }}
                      >
                        <Label
                          text={t("exrate_option")}
                          location="top"
                          visible={false}
                        />
                      </SimpleItem>
                    </GroupItem>
                    <GroupItem colCount={1}>
                      <GroupItem>
                        <GroupItem
                          visible={
                            collectRef.current?.instance().option("formData")
                              .OPTIUNE === -1
                          }
                        >
                          <SimpleItem
                            dataField="OPTIUNE_FORM"
                            render={() =>
                              currencyForm(closestRate as TCurrencyRecordSet)
                            }
                          >
                            <Label
                              text={closestRate.XRDATESTRING}
                              location="top"
                              alignment="left"
                              visible={true}
                            />
                          </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                          <SimpleItem
                            dataField="OPTIUNE_FORM"
                            visible={
                              collectRef.current?.instance().option("formData")
                                .OPTIUNE === 0
                            }
                            render={() => currencyForm(ivFiscalRate)}
                          >
                            <Label
                              text={t("invoicefiscal_rate")}
                              location="top"
                              alignment="left"
                              visible={true}
                            />
                          </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                          <SimpleItem
                            dataField="OPTIUNE_FORM"
                            visible={
                              collectRef.current?.instance().option("formData")
                                .OPTIUNE === 1
                            }
                            render={() => currencyForm(ivProformaRate)}
                          >
                            <Label
                              text={t("invoiceproforma_rate")}
                              location="top"
                              alignment="left"
                              visible={true}
                            />
                          </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                          <SimpleItem
                            dataField="MONEDA_CURS_SPECIAL"
                            visible={
                              collectRef.current?.instance().option("formData")
                                .OPTIUNE === 2
                            }
                            editorType="dxSelectBox"
                            editorOptions={{
                              items: ["RON", "EUR", "USD"],
                              showClearButton: true,
                              useMaskBehavior: true,
                            }}
                          >
                            <Label
                              text={t("custom_rate_currency")}
                              location="top"
                              alignment="left"
                              visible={true}
                            />
                          </SimpleItem>
                          <SimpleItem
                            dataField="CURS_SPECIAL"
                            visible={
                              collectRef.current?.instance().option("formData")
                                .OPTIUNE === 2
                            }
                            editorType="dxNumberBox"
                            editorOptions={{
                              format: "###,##0.0000",
                              showClearButton: true,
                              useMaskBehavior: true,
                            }}
                          >
                            <Label
                              text={t("custom_rate_value")}
                              location="top"
                              alignment="left"
                              visible={true}
                            />
                          </SimpleItem>
                        </GroupItem>
                      </GroupItem>
                    </GroupItem>
                  </GroupItem>
                  <GroupItem
                    colSpan={2}
                    colCount={4}
                    caption="."
                    cssClass="gi-no-text"
                  >
                    <EmptyItem />
                    <EmptyItem />
                    <ButtonItem
                      visible={isNewRecordMode}
                      buttonOptions={saveButtonOptions}
                    />
                    <ButtonItem
                      //delete button
                      visible={!isNewRecordMode}
                      buttonOptions={{
                        text: "Stergerea incasarii",
                        type: "danger",
                        useSubmitBehavior: false,
                        onClick: async (e: any) => {
                          listenRef.current = true;
                          const ldata = {
                            ...dataSet,
                            DATA_I: format(
                              new Date(dataSet.DATA_I),
                              "yyyy-MM-dd"
                            ),
                          };
                          dispatch(
                            deleteFiscalInvoicePayment(ldata as TCollectForm)
                          );
                        },
                      }}
                    />

                    <ButtonItem
                      buttonOptions={{
                        text: "Anularea operatiunii",
                        type: "normal",
                        useSubmitBehavior: true,
                        onClick: () => {
                          dispatch([
                            setEditingCollection({ iid: null }),
                            applog({
                              operation: EnuAppOps.collection_end_editing,
                              value: sumDataset,
                            }),
                          ]);
                        },
                      }}
                    />
                  </GroupItem>
                </Tab>
                <Tab title="Istoric">
                  <SimpleItem
                    render={(e) =>
                      data.FF_ID > 0 ? (
                        <IFCollectionHistory ffid={data.FF_ID} />
                      ) : (
                        <div>no invoice selected</div>
                      )
                    }
                  />
                </Tab>
              </TabbedItem>
            </GroupItem>
          </Form>
        </div>
      </CaseElse>
    </Switch>
  );
};
